import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { useFirestore } from '../contexts/FirestoreContext';
import { useRealTimeDashboardTotals, useStaffMetricsHardened } from './useRealTimeDashboardTotals';

/**
 * UPDATED: Custom hook for staff safety metrics from Active/Stabilising incidents only
 * Aggregates staff status from c3_staff_status collection for current operational incidents
 * 
 * ⚠️ LEGACY BEHAVIOR CHANGED: Now filters to Active/Stabilising incidents only
 * Use useRealTimeDashboardTotals for more advanced implementations
 */
export const useStaffMetrics = (orgId) => {
  const { db } = useFirestore();
  const [metrics, setMetrics] = useState({
    safe: 0,
    need_assistance: 0,
    unaccounted: 0,
    hospitalised: 0,
    deceased: 0,
    loading: true,
    error: null
  });

  useEffect(() => {
    if (!orgId || !db) {
      setMetrics(prev => ({ ...prev, loading: false }));
      return;
    }

    console.log('🧍 DIRECTLY QUERYING ALL STAFF STATUS for orgId:', orgId);
    
    // FIXED: Query staff status directly, no incident dependency
    const staffRef = collection(db, 'c3_staff_status');
    const staffQuery = query(
      staffRef,
      where('orgId', '==', orgId)
    );

    const unsubscribeStaff = onSnapshot(staffQuery,
          (staffSnapshot) => {
            const staffData = staffSnapshot.docs.map(doc => doc.data());
            
            // Aggregate by status with mobile app mapping
            const aggregated = staffData.reduce((acc, staff) => {
              let status = staff.status || 'unaccounted';
              
              // Map mobile app status values to dashboard status keys
              const statusMap = {
                'Help': 'need_assistance',
                'Safe': 'safe', 
                'Unaccounted For': 'unaccounted',
                'Hospitalised': 'hospitalised',
                'Deceased': 'deceased'
              };
              
              // Convert mobile status to dashboard status
              const dashboardStatus = statusMap[status] || status.toLowerCase().replace(/\s+/g, '_');
              
              if (acc.hasOwnProperty(dashboardStatus)) {
                acc[dashboardStatus] = (acc[dashboardStatus] || 0) + 1;
              } else {
                acc['unaccounted'] = (acc['unaccounted'] || 0) + 1; // Default unknown statuses to unaccounted
              }
              
              return acc;
            }, {
              safe: 0,
              need_assistance: 0,
              unaccounted: 0,
              hospitalised: 0,
              deceased: 0
            });

            console.log('🧍 STAFF METRICS DEBUG:', {
              rawStaffData: staffData.map(s => ({ status: s.status, orgId: s.orgId, incidentId: s.incidentId })),
              aggregated,
              timestamp: new Date().toISOString()
            });
            setMetrics({
              ...aggregated,
              loading: false,
              error: null
            });
          },
          (error) => {
            console.error('❌ CANARY: Staff metrics onSnapshot ERROR:', error, error.code, error.message);
            setMetrics(prev => ({
              ...prev,
              loading: false,
              error: `${error.code}: ${error.message}`
            }));
          }
    );

    return () => {
      unsubscribeStaff();
    };
  }, [orgId, db]);

  return metrics;
};

/**
 * UPDATED: Custom hook for site operational status metrics from Active/Stabilising incidents only
 * Uses site status updates from current operational incidents rather than all organizational sites
 * 
 * ⚠️ LEGACY BEHAVIOR CHANGED: Now filters to Active/Stabilising incidents only
 */
export const useSiteMetrics = (orgId) => {
  const { db } = useFirestore();
  const [metrics, setMetrics] = useState({
    active: 0,
    degraded: 0,
    evacuating: 0,
    evacuated: 0,
    closed_and_secure: 0,
    unknown: 0,
    loading: true,
    error: null
  });

  useEffect(() => {
    if (!orgId || !db) {
      setMetrics(prev => ({ ...prev, loading: false }));
      return;
    }

    console.log('🏢 Setting up site metrics listener for ALL organization sites:', orgId);
    
    // 🔄 REVERTED: Query all organization sites (like it used to work)
    const sitesRef = collection(db, 'c3_sites');
    const sitesQuery = query(
      sitesRef,
      where('organization_id', '==', orgId)
    );

    const unsubscribeSites = onSnapshot(sitesQuery,
      (sitesSnapshot) => {
        const siteData = sitesSnapshot.docs.map(doc => doc.data());
        
        // Aggregate by status
        const aggregated = siteData.reduce((acc, site) => {
          const status = site.status || 'unknown';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {
          active: 0,
          degraded: 0,
          evacuating: 0,
          evacuated: 0,
          closed_and_secure: 0,
          unknown: 0
        });

        console.log('🏢 Site metrics updated (ALL organization sites):', aggregated);
        setMetrics({
          ...aggregated,
          loading: false,
          error: null
        });
      },
      (error) => {
        console.error('❌ Site metrics error:', error);
        setMetrics(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
      }
    );

    return () => {
      unsubscribeSites();
    };
  }, [orgId, db]);

  return metrics;
};

/**
 * Custom hook for incident type breakdown
 * Shows active incidents and exercises with real-time count updates
 */
export const useIncidentTypeMetrics = (orgId) => {
  const { db } = useFirestore();
  const [metrics, setMetrics] = useState({
    types: [],
    totalCount: 0,
    loading: true,
    error: null
  });

  useEffect(() => {
    if (!orgId || !db) {
      setMetrics(prev => ({ ...prev, loading: false }));
      return;
    }

    console.log('📊 Setting up incident type metrics listener for org:', orgId);

    const incidentsRef = collection(db, 'c3_incidents');
    const q = query(
      incidentsRef,
      where('orgId', '==', orgId)
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const incidentsData = snapshot.docs.map(doc => doc.data());
        
        // Filter to include only active and stabilising incidents
        // Only show incidents with status: 'Active' or 'Stabilising'
        const activeIncidents = incidentsData.filter(incident => {
          const status = incident.status;
          return status === 'Active' || status === 'Stabilising';
        });
        
        // Calculate total count
        const totalCount = activeIncidents.length;
        
        // Aggregate by crisis type
        const typeCount = activeIncidents.reduce((acc, incident) => {
          const type = incident.crisisType || incident.customCrisisType || 'Other';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {});

        // Convert to array and sort by count (show all types, not just top 3)
        const typesArray = Object.entries(typeCount)
          .map(([type, count]) => ({ type, count }))
          .sort((a, b) => b.count - a.count);

        console.log('📊 Incident type metrics updated (Active & Exercise only):', {
          totalCount,
          types: typesArray,
          activeIncidents: activeIncidents.map(i => ({ title: i.title, status: i.status, type: i.crisisType }))
        });
        
        setMetrics({
          types: typesArray,
          totalCount: totalCount,
          loading: false,
          error: null
        });
      },
      (error) => {
        console.error('❌ Incident type metrics error:', error);
        setMetrics(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
      }
    );

    return () => unsubscribe();
  }, [orgId, db]);

  return metrics;
};

/**
 * Custom hook for last human activity
 * Filters out system/AI activities to show only human actions
 */
export const useLastHumanActivity = (orgId) => {
  const { db } = useFirestore();
  const [activity, setActivity] = useState({
    data: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    if (!orgId || !db) {
      setActivity(prev => ({ ...prev, loading: false }));
      return;
    }

    console.log('👤 Setting up last human activity listener for org:', orgId);

    const logsRef = collection(db, 'c3_incident_logs');
    const q = query(
      logsRef,
      where('orgId', '==', orgId),
      orderBy('timestamp', 'desc'),
      limit(50) // Get recent entries to filter
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const logs = snapshot.docs.map(doc => doc.data());
        
        // Sort by timestamp descending (client-side)
        const sortedLogs = logs.sort((a, b) => {
          const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
          const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
          return timeB - timeA;
        });
        
        // Filter out system/AI activities but include more human activities
        const humanActivity = sortedLogs.find(log => {
          // Exclude system users and AI/automation activities
          const isHumanUser = log.userId && 
                             log.userId !== 'System' && 
                             log.userId !== 'system' &&
                             log.userFullName && 
                             log.userFullName !== 'System' &&
                             log.userFullName !== 'SMS Site Status System';
          
          const isSystemActivity = log.action?.includes('ai_') ||
                                  log.action?.includes('automation_') ||
                                  log.action?.includes('sms_reply') ||
                                  log.action?.includes('sms_response') ||
                                  log.details?.includes('Auto-created') ||
                                  log.details?.includes('via SMS reply');
          
          return isHumanUser && !isSystemActivity;
        });

        console.log('👤 Last human activity updated:', humanActivity);
        setActivity({
          data: humanActivity,
          loading: false,
          error: null
        });
      },
      (error) => {
        console.error('❌ Last human activity error:', error);
        setActivity(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
      }
    );

    return () => unsubscribe();
  }, [orgId, db]);

  return activity;
};

/**
 * NEW: Hardened dashboard metrics using incident-based aggregation
 * Exports the new hooks for components that need to migrate
 */

// Re-export hardened hooks for easy migration
export { useRealTimeDashboardTotals } from './useRealTimeDashboardTotals';
export { useStaffMetricsHardened } from './useRealTimeDashboardTotals';

/**
 * Migration helper: Use this instead of useStaffMetrics
 * Drop-in replacement with same interface but fixes inheritance bug
 */
export const useStaffMetricsFixed = (orgId) => {
  return useStaffMetricsHardened(orgId);
}; 